/**
 * «Привязать игру» в карточке человека (DC → Юнифанс → Аудитория).
 *
 * Нужно там, где автоматика бессильна: человек бронировал с одного номера, а Telegram
 * у него на другом, либо бронь оформлял капитан команды. Таких аккаунтов около семидесяти,
 * и до сих пор их прицепляли запросом в базу руками.
 *
 * Выбор игры — по дате, а не по номеру брони: id никто не помнит, а «был на Психушке
 * вчера в 20:30» знают всегда.
 */
import { useCallback, useState, type CSSProperties, type ReactElement } from "react";
import { api } from "../../api/client";
import { PlayersBadge } from "../../components/shared";

interface DayBooking {
  id: number;
  date: string;
  time: string;
  quest: string;
  players: number | null;
  videos: number;
  taken_by: number;
}

const box: CSSProperties = { border: "1px solid var(--surface-3)", borderRadius: 8, padding: 8, display: "grid", gap: 7 };
const inp: CSSProperties = { padding: "5px 8px", borderRadius: 6, border: "1px solid var(--surface-3)", background: "var(--surface-1)", color: "var(--text-1)", fontSize: 12.5, fontFamily: "inherit" };
const btn: CSSProperties = { padding: "5px 10px", borderRadius: 6, border: "1px solid var(--surface-3)", background: "var(--surface-2)", color: "var(--text-2)", fontSize: 12, cursor: "pointer" };

/** Сегодня по бизнес-часовому поясу UTC+5, а не по времени браузера. */
function todayBiz(): string {
  return new Date(Date.now() + 5 * 3600_000).toISOString().slice(0, 10);
}

export function DcAttachGame({ tg, onDone, onToast }: {
  tg: number;
  onDone: () => void;
  onToast: (m: string) => void;
}): ReactElement {
  const [date, setDate] = useState(todayBiz());
  const [list, setList] = useState<DayBooking[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async (d: string) => {
    setLoading(true);
    setList(null);
    try {
      const r = await api<{ bookings: DayBooking[] }>(`/api/audience/bookings?date=${d}`);
      setList(r.bookings ?? []);
    } catch {
      setList([]);
      onToast("Не удалось загрузить игры дня");
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  const attach = async (b: DayBooking): Promise<void> => {
    setBusyId(b.id);
    try {
      const r = await api<{ ok: true; already?: boolean }>(`/api/audience/${tg}/attach`, {
        method: "POST",
        body: { bookingId: b.id },
      });
      onToast(r.already ? "Уже был привязан к этой игре" : `Привязан: ${b.quest} ${b.time}`);
      onDone();
    } catch (e) {
      onToast(e instanceof Error ? e.message : "Ошибка привязки");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={box}>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <input type="date" value={date} style={{ ...inp, width: 140 }}
          onChange={(e) => { setDate(e.target.value); setList(null); }} />
        <button type="button" style={btn} onClick={() => void load(date)} disabled={loading}>
          {loading ? "…" : "Показать игры дня"}
        </button>
      </div>

      {list === null ? null : list.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-3)" }}>В этот день игр нет.</div>
      ) : (
        <div style={{ display: "grid", gap: 4 }}>
          {list.map((b) => (
            <div key={b.id} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center", minHeight: 28 }}>
              <span style={{ fontSize: 12.5, color: "var(--text-1)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {b.time} · {b.quest}
                {/* 05.09 (владелец): стандартный бейдж игроков; b.id = bookings.id, у ДК есть доступ — клик открывает карточку клиента */}
                {b.players ? <> · <PlayersBadge count={b.players} bookingId={b.id} size="sm" /></> : null}
                <span style={{ color: "var(--text-3)" }}>{b.videos ? ` · ${b.videos} видео` : " · без видео"}</span>
              </span>
              <button type="button" style={{ ...btn, background: "transparent", color: "var(--role)" }}
                onClick={() => void attach(b)} disabled={busyId === b.id}>
                {busyId === b.id ? "…" : "привязать"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
